import { useState, useEffect } from 'react'
import { X, MessageSquare, Loader2, Smartphone } from 'lucide-react'
import {
  useCreateWhatsAppMailbox,
  useUpdateWhatsAppMailbox,
} from '@hooks/useWhatsAppMailboxes'
import { WhatsAppMailbox } from '@/types'
import {
  CreateWhatsAppMailboxRequest,
  UpdateWhatsAppMailboxRequest,
} from '@services/whatsapp-mailbox.service'

/**
 * WhatsAppMailboxForm Component
 * QR Code Authentication (Baileys) Configuration Form
 * Version: 2.0.0 (QR Code Authentication Support)
 */

interface WhatsAppMailboxFormProps {
  mailbox?: WhatsAppMailbox | null
  onClose: () => void
  onSuccess: () => void
}

type ConnectionType = 'BUSINESS_API' | 'BAILEYS'

interface FormData {
  // Basic Information
  name: string
  phoneNumber: string
  displayName: string
  businessDescription: string
  connectionType: ConnectionType
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'RESTRICTED'
  webhookUrl: string
  notes: string
}

const DEFAULT_FORM_DATA: FormData = {
  name: '',
  phoneNumber: '',
  displayName: '',
  businessDescription: '',
  connectionType: 'BAILEYS',
  status: 'ACTIVE',
  webhookUrl: '',
  notes: '',
}

export function WhatsAppMailboxForm({ mailbox, onClose, onSuccess }: WhatsAppMailboxFormProps) {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA)

  const createMutation = useCreateWhatsAppMailbox()
  const updateMutation = useUpdateWhatsAppMailbox(mailbox?.id || 0)

  const isEditing = Boolean(mailbox)
  const isLoading = createMutation.isPending || updateMutation.isPending

  // Load existing mailbox data
  useEffect(() => {
    if (mailbox) {
      setFormData({
        name: mailbox.name,
        phoneNumber: mailbox.phoneNumber,
        displayName: mailbox.displayName || '',
        businessDescription: mailbox.businessDescription || '',
        connectionType: mailbox.connectionType,
        status: mailbox.status,
        webhookUrl: mailbox.webhookUrl || '',
        notes: mailbox.notes || '',
      })
    }
  }, [mailbox])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const requestData: CreateWhatsAppMailboxRequest | UpdateWhatsAppMailboxRequest = {
      name: formData.name,
      phoneNumber: formData.phoneNumber,
      displayName: formData.displayName || undefined,
      businessDescription: formData.businessDescription || undefined,
      connectionType: formData.connectionType,
      webhookUrl: formData.webhookUrl || undefined,
      notes: formData.notes || undefined,
    }

    // Add status for updates
    if (isEditing) {
      ;(requestData as UpdateWhatsAppMailboxRequest).status = formData.status
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(requestData as UpdateWhatsAppMailboxRequest)
      } else {
        await createMutation.mutateAsync(requestData as CreateWhatsAppMailboxRequest)
      }
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Failed to save WhatsApp mailbox:', error)
    }
  }

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageSquare className="text-primary" size={24} />
            <h2 className="text-xl font-bold text-gray-900">
              {isEditing ? 'Edit WhatsApp Mailbox' : 'Add New WhatsApp Mailbox'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Customer Support WhatsApp"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phoneNumber}
                  onChange={(e) => updateField('phoneNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="+1234567890"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => updateField('displayName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Support Team"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Description
                </label>
                <input
                  type="text"
                  value={formData.businessDescription}
                  onChange={(e) => updateField('businessDescription', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., 24/7 Customer Support"
                />
              </div>
            </div>

            {isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    updateField('status', e.target.value as FormData['status'])
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="RESTRICTED">Restricted</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Webhook URL
              </label>
              <input
                type="url"
                value={formData.webhookUrl}
                onChange={(e) => updateField('webhookUrl', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="https://your-domain.com/whatsapp/webhook"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional: Webhook URL to receive WhatsApp notifications
              </p>
            </div>
          </div>

          {/* Connection Type Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Connection Type</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Authentication Method <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => updateField('connectionType', 'BAILEYS')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.connectionType === 'BAILEYS'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Smartphone size={24} className="mx-auto mb-2" />
                  <div className="font-medium">QR Code</div>
                  <div className="text-xs text-gray-500">Baileys (Recommended)</div>
                </button>

                <button
                  type="button"
                  onClick={() => updateField('connectionType', 'BUSINESS_API')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.connectionType === 'BUSINESS_API'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <MessageSquare size={24} className="mx-auto mb-2" />
                  <div className="font-medium">Business API</div>
                  <div className="text-xs text-gray-500">Official WhatsApp API</div>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                QR Code: Free, scan with your phone. Business API: Paid, requires Meta approval.
              </p>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Optional notes about this WhatsApp mailbox..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>{isEditing ? 'Update Mailbox' : 'Create Mailbox'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
